import React, { useState, useEffect } from "react";
import classNames from "classnames/bind";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import styles from "./authorization.module.scss";
import { useDispatch } from "react-redux";
import { togglePopup } from "../../slices/utilsSlice";
import { addToast } from "../../slices/toastSlice";
import { authService } from "./authService";
import { signInWithGoogle } from "../../cognitoConfig";
import { LanguageSelector } from "../language-selector/LanguageSelector";
import { useValidateInviteMutation, useAcceptInviteMutation } from "../../api/authApi";
import { isCognitoError, getErrorMessage, isRtkQueryError, getAuthErrorKey } from "../../utils/error-utils";
import { trackLogin, trackSignUp } from "../../utils/analytics";

/** Wrap plain objects in a proper Error so Sentry shows a useful message */
const toSentryError = (err: unknown): Error => {
  if (err instanceof Error) return err;
  if (typeof err === "object" && err !== null) {
    const msg = (err as Record<string, unknown>).message || (err as Record<string, unknown>).error || JSON.stringify(err);
    const wrapped = new Error(String(msg));
    wrapped.name = (err as Record<string, unknown>).name as string || "NonErrorException";
    (wrapped as unknown as Record<string, unknown>).originalError = err;
    return wrapped;
  }
  return new Error(String(err));
};
import { usePasswordStrength } from "./usePasswordStrength";
import { useAuthValidation } from "./useAuthValidation";
import { BrandingPanel } from "./BrandingPanel";
import { SignInView } from "./views/SignInView";
import { SignUpView } from "./views/SignUpView";
import { VerifyEmailView } from "./views/VerifyEmailView";
import { ForgotPasswordView } from "./views/ForgotPasswordView";
import { ResetPasswordView } from "./views/ResetPasswordView";
import { InviteView } from "./views/InviteView";

type AuthView = "signin" | "signup" | "verify" | "forgot" | "reset" | "invite";

const cx = classNames.bind(styles);

interface AuthorizationProps {
  initialView?: "signin" | "signup";
}

const Authorization: React.FC<AuthorizationProps> = ({ initialView }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Form state
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [view, setView] = useState<AuthView>(initialView || "signin");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Invite flow state
  const [inviteToken, setInviteToken] = useState("");
  const [inviteValidating, setInviteValidating] = useState(false);
  const [inviteTokenValid, setInviteTokenValid] = useState<boolean | null>(null);
  const [validateInvite] = useValidateInviteMutation();
  const [acceptInvite] = useAcceptInviteMutation();

  // Custom hooks
  const strengthPassword = view === "reset" ? newPassword : password;
  const { strength: passwordStrength, strengthColor, strengthText } = usePasswordStrength(strengthPassword);
  const validation = useAuthValidation();

  // Detect /accept-invite route and validate token
  useEffect(() => {
    if (location.pathname === "/accept-invite") {
      const token = searchParams.get("token") || "";
      if (!token) {
        setView("signin");
        return;
      }
      setInviteToken(token);
      setInviteValidating(true);
      setView("invite");

      validateInvite({ token })
        .unwrap()
        .then((result) => {
          setEmail(result.email);
          if (result.displayName) setDisplayName(result.displayName);
          setInviteTokenValid(true);
        })
        .catch(() => {
          setInviteTokenValid(false);
          setAuthError("inviteTokenInvalid");
        })
        .finally(() => setInviteValidating(false));
    }
  }, []);

  // Clear errors when switching views
  useEffect(() => {
    setAuthError(null);
    validation.clearErrors();
  }, [view]);

  // --- Handlers ---

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAuthError(null);
    if (!validation.validateInputs({ view, email, password, confirmPassword })) return;

    setIsLoading(true);
    try {
      await authService.signIn(email, password);
      trackLogin("email");
    } catch (err: unknown) {
      console.error("Sign in error:", err);
      if (isCognitoError(err) && err.code === "UserNotConfirmed") {
        setView("verify");
        authService.resendConfirmationCode(email).then(() => {
          dispatch(addToast({ type: "success", message: t("verificationCodeSent") }));
        }).catch((err) => { Sentry.captureException(toSentryError(err)); });
      } else if (isCognitoError(err) && err.code === "InvalidCredentials") {
        setAuthError("auth.errors.wrongPassword");
      } else {
        const key = getAuthErrorKey(err);
        if (key) {
          setAuthError(`auth.errors.${key}`);
        } else {
          Sentry.captureException(toSentryError(err));
          setAuthError("auth.errors.genericError");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAuthError(null);
    if (!validation.validateInputs({ view, email, password, confirmPassword })) return;

    setIsLoading(true);
    try {
      const result = await authService.signUp(email, password);
      if (!result.userConfirmed) {
        setView("verify");
        dispatch(addToast({ type: "success", message: t("verificationCodeSent") }));
      }
    } catch (err: unknown) {
      // sign_up fires after email verification (handleVerifyCode), not here
      console.error("Sign up error:", err);
      if (isCognitoError(err) && err.code === "EmailExists") {
        setAuthError("emailAlreadyExists");
      } else if (isCognitoError(err) && err.code === "WeakPassword") {
        setAuthError("passwordTooWeak");
      } else {
        const key = getAuthErrorKey(err);
        if (key) {
          setAuthError(`auth.errors.${key}`);
        } else {
          Sentry.captureException(toSentryError(err));
          setAuthError("auth.errors.genericError");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!verificationCode) {
      validation.setCodeError(true);
      return;
    }

    setIsLoading(true);
    setAuthError(null);
    try {
      await authService.confirmSignUp(email, verificationCode);
      await authService.signIn(email, password);
      trackSignUp("email");
    } catch (err: unknown) {
      console.error("Verification error:", err);
      if (isCognitoError(err) && err.code === "InvalidCode") {
        setAuthError("invalidVerificationCode");
      } else if (isCognitoError(err) && err.code === "ExpiredCode") {
        setAuthError("verificationCodeExpired");
      } else {
        const key = getAuthErrorKey(err);
        if (key) {
          setAuthError(`auth.errors.${key}`);
        } else {
          Sentry.captureException(toSentryError(err));
          setAuthError("auth.errors.genericError");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      await authService.resendConfirmationCode(email);
      dispatch(addToast({ type: "success", message: t("verificationCodeResent") }));
    } catch (err: unknown) {
      const key = getAuthErrorKey(err);
      const message = key ? t(`auth.errors.${key}`) : t("auth.errors.genericError");
      dispatch(togglePopup({ type: "ERROR", message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) {
      validation.setEmailError(true);
      return;
    }

    setIsLoading(true);
    setAuthError(null);
    try {
      await authService.forgotPassword(email);
      setView("reset");
      dispatch(addToast({ type: "success", message: t("passwordResetCodeSent") }));
    } catch (err: unknown) {
      console.error("Forgot password error:", err);
      if (isCognitoError(err) && err.code === "LimitExceededException") {
        setAuthError("auth.errors.tooManyAttempts");
      } else if (isCognitoError(err) && err.code === "UserNotFoundException") {
        setAuthError("auth.errors.userNotFound");
      } else if (isCognitoError(err) && err.code === "GoogleOnlyUser") {
        setAuthError("googleOnlyUser");
      } else {
        const key = getAuthErrorKey(err);
        if (key) {
          setAuthError(`auth.errors.${key}`);
        } else {
          Sentry.captureException(toSentryError(err));
          setAuthError("auth.errors.genericError");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!verificationCode) {
      validation.setCodeError(true);
      return;
    }
    if (!newPassword) {
      validation.setPasswordError(true);
      return;
    }

    setIsLoading(true);
    setAuthError(null);
    try {
      await authService.confirmForgotPassword(email, verificationCode, newPassword);
      dispatch(addToast({ type: "success", message: t("passwordResetSuccess") }));
      setView("signin");
      setPassword("");
      setNewPassword("");
      setVerificationCode("");
    } catch (err: unknown) {
      console.error("Reset password error:", err);
      if (isCognitoError(err) && err.code === "CodeMismatchException") {
        setAuthError("invalidResetCode");
      } else if (isCognitoError(err) && err.code === "ExpiredCodeException") {
        setAuthError("resetCodeExpired");
      } else if (isCognitoError(err) && err.code === "LimitExceededException") {
        setAuthError("auth.errors.tooManyAttempts");
      } else if (isCognitoError(err) && err.code === "InvalidPasswordException") {
        setAuthError("passwordTooWeak");
      } else {
        const key = getAuthErrorKey(err);
        if (key) {
          setAuthError(`auth.errors.${key}`);
        } else {
          Sentry.captureException(toSentryError(err));
          setAuthError("auth.errors.genericError");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAuthError(null);
    if (!validation.validateInputs({ view, email, password, confirmPassword })) return;

    setIsLoading(true);
    try {
      await acceptInvite({ token: inviteToken, password, displayName: displayName || undefined }).unwrap();
      await authService.signIn(email, password);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      console.error("Invite accept error:", err);
      if (isRtkQueryError(err) && (err.data as Record<string, unknown>)?.error) {
        const nestedError = (err.data as Record<string, Record<string, unknown>>).error;
        if (typeof nestedError?.message === "string") {
          setAuthError(nestedError.message);
        } else if (typeof (err.data as Record<string, unknown>)?.message === "string") {
          setAuthError((err.data as Record<string, unknown>).message as string);
        }
      } else if (isRtkQueryError(err) && typeof (err.data as Record<string, unknown>)?.message === "string") {
        setAuthError((err.data as Record<string, unknown>).message as string);
      } else if (isCognitoError(err) && (err.code === "InvalidCredentials" || err.code === "NewPasswordRequired")) {
        dispatch(addToast({ type: "success", message: t("invitePasswordSet") }));
        navigate("/", { replace: true });
        setView("signin");
      } else {
        const key = getAuthErrorKey(err);
        if (key) {
          setAuthError(`auth.errors.${key}`);
        } else {
          Sentry.captureException(toSentryError(err));
          setAuthError("auth.errors.genericError");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- Render helpers ---

  const handleEmailChange = (value: string) => {
    setEmail(value);
    validation.setEmailError(false);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    validation.setPasswordError(false);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    validation.setConfirmPasswordError(false);
  };

  const handleCodeChange = (value: string) => {
    setVerificationCode(value);
    validation.setCodeError(false);
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    validation.setPasswordError(false);
  };

  const backToSignIn = () => setView("signin");

  const renderViewContent = () => {
    switch (view) {
      case "invite":
        return (
          <InviteView
            email={email}
            displayName={displayName}
            password={password}
            confirmPassword={confirmPassword}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            passwordError={validation.passwordError}
            confirmPasswordError={validation.confirmPasswordError}
            authError={authError}
            isLoading={isLoading}
            inviteValidating={inviteValidating}
            inviteTokenValid={inviteTokenValid}
            passwordStrength={passwordStrength}
            passwordStrengthColor={strengthColor}
            passwordStrengthText={strengthText}
            onDisplayNameChange={setDisplayName}
            onPasswordChange={handlePasswordChange}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            onBackToSignIn={backToSignIn}
            onSubmit={handleInviteSubmit}
          />
        );
      case "verify":
        return (
          <VerifyEmailView
            verificationCode={verificationCode}
            codeError={validation.codeError}
            authError={authError}
            isLoading={isLoading}
            onCodeChange={handleCodeChange}
            onResendCode={handleResendCode}
            onBackToSignIn={backToSignIn}
            onSubmit={handleVerifyCode}
          />
        );
      case "forgot":
        return (
          <ForgotPasswordView
            email={email}
            emailError={validation.emailError}
            authError={authError}
            isLoading={isLoading}
            onEmailChange={handleEmailChange}
            onBackToSignIn={backToSignIn}
            onSubmit={handleForgotPassword}
          />
        );
      case "reset":
        return (
          <ResetPasswordView
            verificationCode={verificationCode}
            newPassword={newPassword}
            showNewPassword={showNewPassword}
            codeError={validation.codeError}
            passwordError={validation.passwordError}
            authError={authError}
            isLoading={isLoading}
            passwordStrength={passwordStrength}
            passwordStrengthColor={strengthColor}
            passwordStrengthText={strengthText}
            onCodeChange={handleCodeChange}
            onNewPasswordChange={handleNewPasswordChange}
            onToggleNewPassword={() => setShowNewPassword(!showNewPassword)}
            onBackToSignIn={backToSignIn}
            onSubmit={handleResetPassword}
          />
        );
      case "signup":
        return (
          <SignUpView
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            emailError={validation.emailError}
            passwordError={validation.passwordError}
            confirmPasswordError={validation.confirmPasswordError}
            authError={authError}
            isLoading={isLoading}
            passwordStrength={passwordStrength}
            passwordStrengthColor={strengthColor}
            passwordStrengthText={strengthText}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            onSwitchToSignIn={backToSignIn}
            onGoogleSignIn={() => signInWithGoogle()}
            onSubmit={handleSignUp}
          />
        );
      case "signin":
      default:
        return (
          <SignInView
            email={email}
            password={password}
            rememberMe={rememberMe}
            showPassword={showPassword}
            emailError={validation.emailError}
            passwordError={validation.passwordError}
            authError={authError}
            isLoading={isLoading}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            onRememberMeChange={setRememberMe}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onForgotPassword={() => setView("forgot")}
            onSwitchToSignUp={() => setView("signup")}
            onGoogleSignIn={() => signInWithGoogle()}
            onSubmit={handleSignIn}
          />
        );
    }
  };

  return (
    <div className={cx('authContainer')}>
      <BrandingPanel view={view} />
      <div className={cx('formPanel')}>
        <div className={cx('authLangSelector')}>
          <LanguageSelector />
        </div>
        {renderViewContent()}
      </div>
    </div>
  );
};

export default Authorization;
